import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";

import ECGHeart from "../../icons/ECGHeart";
import Spacer from "../../Spacer";
import PoincareChart from "../../charts/PoincareChart";

import {
  isPassingHrSignalQuality,
  pickHrSignalQuality,
} from "./quality";
import { pickHeartDxHrBpm } from "./pickHrBpm";
import { formatValidatedMeasurement } from "../../../utils/results/validatedRanges";

function getPulseQualityMessage(quality, t) {
  if (quality != null && !isPassingHrSignalQuality(quality)) {
    return {
      title: t("result.pulse.qualityFailTitle", "Low HR signal quality."),
      subtitle: t("result.pulse.qualityFailSubtitle", "Please try again."),
    };
  }

  return null;
}

function PulseScanResultViewImpl({ data, onFinish }) {
  const { t } = useTranslation();

  // const hrCpp = useMemo(() => pickHrBpm(data), [data]);
  const hrHeartDx = useMemo(() => pickHeartDxHrBpm(data), [data]);
  const hrQuality = useMemo(() => pickHrSignalQuality(data), [data]);

  const qualityMsg = useMemo(
    () => getPulseQualityMessage(hrQuality, t),
    [hrQuality, t],
  );
  const hrDisplay = useMemo(
    () => formatValidatedMeasurement("hr", hrHeartDx),
    [hrHeartDx],
  );

  const px = data?.heartdx?.poincare?.x || [];
  const py = data?.heartdx?.poincare?.y || [];

  return (
    <div className="w-full max-w-lg h-full min-h-0 overflow-y-auto">
      <section className="min-h-full p-4 flex flex-col items-center text-center gap-4">
        <h1 className="subtitle-text">
          {t("result.pulse.completeTitle", "Pulse Scan complete")}
        </h1>

        <div className="flex flex-row items-center gap-6">
          <div
            className={[
              "w-14 h-14 flex items-center justify-center",
              qualityMsg ? "fill-greyscale-3" : "text-primary fill-primary",
            ].join(" ")}
          >
            <ECGHeart className="w-full h-full" />
          </div>

          <div className="flex flex-col items-center">
            <div className="flex flex-col items-center justify-center">
              <div className="">{t("result.pulse.hrLabel", "Heart Rate")}</div>
              <div
                className={[
                  "supertitle-text",
                  qualityMsg ? "!text-greyscale-3" : "text-primary",
                ].join(" ")}
              >
                {hrDisplay}
                <span className="muted-text">
                  {t("result.pulse.unit", "bpm")}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* 
        <div className="flex flex-row items-center gap-6">
          <div
            className={[
              "w-14 h-14 flex items-center justify-center",
              qualityMsg ? "fill-greyscale-3" : "text-primary fill-primary",
            ].join(" ")}
          >
            <ECGHeart className="w-full h-full" />
          </div>

          <div className="flex flex-col items-center">
            <div className="flex flex-col items-center justify-center">
              <div className="">{t("result.pulse.hrLabel", "Heart Rate")}</div>
              <div
                className={[
                  "supertitle-text",
                  qualityMsg ? "!text-greyscale-3" : "text-primary",
                ].join(" ")}
              >
                {hrCpp ?? "--"}
                <span className="muted-text">
                  {t("result.pulse.unit", "bpm")} (c++)
                </span>
              </div>
            </div>
          </div>
        </div> */}

        {qualityMsg ? (
          <div className="flex flex-col items-center gap-1">
            <div className="muted-text">{qualityMsg.title}</div>
            <div className="muted-text">{qualityMsg.subtitle}</div>
            {qualityMsg?.text ? (
              <div className="muted-text">{qualityMsg.text}</div>
            ) : null}
          </div>
        ) : null}

        <Spacer />

        <div className="mt-auto w-full pb-2">
          <button
            type="button"
            className="primary-button w-full"
            onClick={onFinish}
          >
            {t("result.finish", "Finish")}
          </button>
        </div>
      </section>

      {/* <section className="p-4 pt-2">
        <PoincareChart x={px} y={py} height={280} initialMode="basic" />
      </section> */}
    </div>
  );
}

const PulseScanResultView = memo(PulseScanResultViewImpl);

// attach statics to the *exported* component
PulseScanResultView.isReady = (data) => pickHeartDxHrBpm(data) != null;

export default PulseScanResultView;
